import { useNavigate } from 'react-router-dom';
import { Compass, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <Compass className="w-7 h-7 text-[#2563EB]" />
              <span className="font-bold text-xl tracking-tight font-['Manrope']">Klarvi</span>
            </button>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="privacy-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="privacy-page">
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-['Manrope'] mb-2">
          Aviso de Privacidad
        </h1>
        <p className="text-sm text-muted-foreground mb-8">Klarvi — Fecha de última actualización: 30 de junio de 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-[#0F172A] text-[15px] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Responsable del tratamiento de datos personales</h2>
            <p>
              Klarvi es la plataforma operada por PAYNE ADMINISTRATIVO, S.A. DE C.V. (RFC: PAD1905301TA), responsable del tratamiento de los datos personales que usted nos proporciona a través de la plataforma Klarvi.
            </p>
            <p className="mt-2">
              Domicilio: Calle Renato Leduc 174, Interior 301, Colonia Toriello Guerra, Alcaldía Tlalpan, C.P. 14050, Ciudad de México.
            </p>
            <p className="mt-2">
              Contacto: <a href="mailto:contacto@klarvi.org" className="text-[#2563EB] hover:underline">contacto@klarvi.org</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Datos personales que recabamos</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Nombre y correo electrónico (al momento del registro)</li>
              <li>Información laboral que usted ingresa voluntariamente (salario, tipo de contrato, situación laboral, consultas al Asesor IA)</li>
              <li>Datos de uso de la Plataforma (funciones utilizadas, frecuencia de acceso)</li>
            </ul>
            <p className="mt-3">
              No recabamos datos sensibles como origen racial, estado de salud, creencias religiosas o datos biométricos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Finalidades del tratamiento</h2>
            <p>Sus datos se usan para:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Proveer los servicios de la Plataforma (Asesor IA, Calculadora de Liquidación, Evaluador de Oferta, Benchmark Salarial)</li>
              <li>Gestionar su cuenta y suscripción</li>
              <li>Enviarle comunicaciones relacionadas con su cuenta</li>
              <li>Mejorar la Plataforma con base en patrones de uso agregados y anónimos</li>
            </ul>
            <p className="mt-3">No usamos sus datos para publicidad de terceros ni los vendemos.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Uso de inteligencia artificial</h2>
            <p>
              El Asesor IA de Klarvi opera mediante la API de OpenAI (modelo gpt-4o-mini). Según la política pública de OpenAI vigente desde marzo de 2023, las consultas realizadas vía API no se utilizan para entrenar sus modelos. Para más información:{' '}
              <a href="https://openai.com/policies/api-data-usage-policies" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">
                openai.com/policies/api-data-usage-policies
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Transferencia de datos</h2>
            <p>Sus datos pueden ser compartidos con:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>OpenAI (procesamiento de consultas al Asesor IA)</li>
              <li>Stripe (procesamiento de pagos)</li>
              <li>Resend (envío de correos transaccionales)</li>
              <li>Emergent (infraestructura tecnológica de la Plataforma)</li>
            </ul>
            <p className="mt-3">No transferimos sus datos a terceros con fines comerciales o de marketing.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Derechos ARCO</h2>
            <p>
              Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales, conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares.
            </p>
            <p className="mt-2">Para ejercerlos, envíe un correo a <a href="mailto:contacto@klarvi.org" className="text-[#2563EB] hover:underline">contacto@klarvi.org</a> con:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Nombre completo</li>
              <li>Correo registrado en Klarvi</li>
              <li>Derecho que desea ejercer y motivo</li>
              <li>Copia de identificación oficial</li>
            </ul>
            <p className="mt-3">Responderemos en un plazo máximo de 20 días hábiles.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Eliminación de cuenta y datos</h2>
            <p>
              Puede solicitar la eliminación de su cuenta y datos en cualquier momento desde la configuración de su perfil, o escribiendo a <a href="mailto:contacto@klarvi.org" className="text-[#2563EB] hover:underline">contacto@klarvi.org</a>. La eliminación es permanente e irreversible.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Cambios a este aviso</h2>
            <p>
              Cualquier modificación se notificará mediante la Plataforma. La versión vigente estará disponible en{' '}
              <span className="font-medium">klarvi.org/privacidad</span>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
